import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useVirtualizer } from "@tanstack/react-virtual"

import type { FeedbackWithRelations } from "@productlane/zero/queries"
import { cn } from "@productlane/lib/src/cn"
import { queries } from "@productlane/zero/queries"

import type {
	DetailThreadsFilters,
	SortOrder,
} from "@/components/detail-screen/detail-threads-toolbar"
import { buildDetailThreadFilters } from "@/components/detail-screen/detail-thread-filters"
import {
	DetailThreadsFilterChips,
	DetailThreadsToolbar,
	EMPTY_DETAIL_FILTERS,
} from "@/components/detail-screen/detail-threads-toolbar"
import { ThreadListItemMultiline } from "@/components/thread-list/thread-list-item"
import { isQuerySettled } from "@/lib/query-settled"
import { offsetWithinScrollParent, scrollParent } from "@/lib/scroll-parent"

// First guess only; rows are measured since tags and previews change card height.
const ITEM_SIZE = 148

export interface DetailThreadsControls {
	sortOrder: SortOrder
	setSortOrder: (order: SortOrder) => void
	filters: DetailThreadsFilters
	setFilters: (filters: DetailThreadsFilters) => void
}

export const useDetailThreadsControls = (): DetailThreadsControls => {
	const [sortOrder, setSortOrder] = useState<SortOrder>("desc")
	const [filters, setFilters] =
		useState<DetailThreadsFilters>(EMPTY_DETAIL_FILTERS)
	return { sortOrder, setSortOrder, filters, setFilters }
}

export const hasActiveDetailFilters = (filters: DetailThreadsFilters) =>
	filters.statuses.length > 0 ||
	filters.origins.length > 0 ||
	filters.assigneeIds.length > 0 ||
	filters.tagIds.length > 0

interface DetailThreadsListProps {
	contactId?: string
	companyId?: string
	projectId?: string
	issueId?: string
	tagId?: string
	assigneeId?: string
	hideLabel?: boolean
	excludeUpvotes?: boolean
	onlyUpvotes?: boolean
	setThreadsSize?: (size: number, isSettled: boolean) => void
	// When passed, the parent owns the toolbar and renders it elsewhere.
	controls?: DetailThreadsControls
	// Drops the list's own side padding so cards line up with the page content.
	flush?: boolean
}

export const DetailThreadsList = ({
	contactId,
	companyId,
	projectId,
	issueId,
	tagId,
	assigneeId,
	setThreadsSize,
	hideLabel = false,
	excludeUpvotes = false,
	onlyUpvotes = false,
	controls,
	flush = false,
}: DetailThreadsListProps) => {
	const [teamMembers] = useQuery(queries.teamMembers())
	const parentRef = useRef<HTMLDivElement>(null)
	const listRef = useRef<HTMLDivElement>(null)
	const [scrollMargin, setScrollMargin] = useState(0)

	const ownControls = useDetailThreadsControls()
	const { sortOrder, setSortOrder, filters, setFilters } =
		controls ?? ownControls

	const { threads: allThreads, isLoading } = useFilteredThreads({
		contactId,
		companyId,
		projectId,
		issueId,
		tagId,
		assigneeId,
		sortOrder,
		filters,
	})

	const threads = useMemo(
		() =>
			onlyUpvotes
				? allThreads.filter((t) => t.origin === "UPVOTE")
				: excludeUpvotes
					? allThreads.filter((t) => t.origin !== "UPVOTE")
					: allThreads,
		[allThreads, excludeUpvotes, onlyUpvotes],
	)

	const detailContext = companyId
		? "company"
		: contactId
			? "contact"
			: projectId
				? "project"
				: issueId
					? "issue"
					: tagId
						? "tag"
						: undefined

	const virtualizer = useVirtualizer({
		count: threads.length,
		getScrollElement: () =>
			parentRef.current ? scrollParent(parentRef.current) : null,
		estimateSize: () => ITEM_SIZE,
		overscan: 5,
		getItemKey: (index) => threads[index]?.id ?? index,
		scrollMargin,
	})

	const showList = threads.length > 0
	useLayoutEffect(() => {
		const list = listRef.current
		const scroller = list ? scrollParent(list) : null
		if (!list || !scroller) return
		const update = () =>
			setScrollMargin(offsetWithinScrollParent(list, scroller))
		update()
		if (typeof ResizeObserver === "undefined") return
		const observer = new ResizeObserver(update)
		observer.observe(scroller.firstElementChild ?? scroller)
		return () => observer.disconnect()
	}, [showList])

	const virtualItems = virtualizer.getVirtualItems()

	const hasActiveFilters = hasActiveDetailFilters(filters)

	useEffect(() => {
		if (setThreadsSize) {
			setThreadsSize(threads.length, !isLoading)
		}
	}, [threads, isLoading, setThreadsSize])

	const showSection = showList || (hasActiveFilters && !isLoading)
	const gutter = flush ? "" : "px-5 sm:px-8"

	return (
		<div ref={parentRef}>
			{showSection && (
				<div className={cn("pb-12", !controls && "mt-4")}>
					{!controls && (
						<div className="flex min-h-7 items-center justify-between px-5 sm:px-8">
							{!hideLabel ? (
								<div className="text-lg font-semibold">Threads</div>
							) : (
								<div />
							)}
							<DetailThreadsToolbar
								sortOrder={sortOrder}
								onSortOrderChange={setSortOrder}
								filters={filters}
								onFiltersChange={setFilters}
								teamMembers={teamMembers}
							/>
						</div>
					)}

					{hasActiveFilters && (
						<div className={cn("mt-2", gutter)}>
							<DetailThreadsFilterChips
								filters={filters}
								onChange={setFilters}
								teamMembers={teamMembers}
							/>
						</div>
					)}

					{showList && (
						<div
							ref={listRef}
							className="relative mt-4"
							style={{ height: `${virtualizer.getTotalSize()}px` }}
						>
							{virtualItems.map((virtualItem) => {
								const thread = threads[virtualItem.index]
								if (!thread) return null

								return (
									<div
										key={virtualItem.key}
										ref={virtualizer.measureElement}
										data-index={virtualItem.index}
										className={cn(
											"absolute left-0 top-0 w-full pb-2.5",
											!flush && "px-5",
										)}
										style={{
											transform: `translateY(${virtualItem.start - scrollMargin}px)`,
										}}
									>
										<ThreadListItemMultiline
											thread={thread}
											withResponseBadge={true}
											teamMembers={teamMembers}
											context={detailContext}
										/>
									</div>
								)
							})}
						</div>
					)}

					{!showList && hasActiveFilters && !isLoading && (
						<div className={cn("mt-6 text-sm text-tertiary", gutter)}>
							No threads match the current filters.
						</div>
					)}
				</div>
			)}
		</div>
	)
}

const useFilteredThreads = ({
	contactId,
	companyId,
	projectId,
	issueId,
	tagId,
	assigneeId,
	sortOrder = "desc",
	filters: userFilters = EMPTY_DETAIL_FILTERS,
}: {
	contactId?: string
	companyId?: string
	projectId?: string
	issueId?: string
	tagId?: string
	assigneeId?: string
	sortOrder?: SortOrder
	filters?: DetailThreadsFilters
}): {
	threads: FeedbackWithRelations[]
	isLoading: boolean
} => {
	const filters = useMemo(
		() =>
			buildDetailThreadFilters({
				contactId,
				companyId,
				projectId,
				issueId,
				tagId,
				assigneeId,
				userFilters,
			}),
		[contactId, companyId, projectId, issueId, tagId, assigneeId, userFilters],
	)

	const [allThreads, queryResult] = useQuery(
		queries.threadSearch({
			filters,
			sortColumn: "lastMessageAt",
			sortOrder,
		}),
	)

	return {
		threads: allThreads,
		isLoading: !isQuerySettled(queryResult),
	}
}
